import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import {
    Box,
    IconButton,
    TableCell,
    TableRow,
    Typography,
} from '@mui/material';
import { alpha } from '@mui/material/styles';

import { getItemUid } from 'entities/CatalogItem/lib/getItemUid';
import { useItemAvailability } from 'entities/CatalogItem/lib/useItemAvailability';
import { CatalogItem } from 'entities/CatalogItem/model/types/CatalogItemSchema';
import { CategoryLabel } from 'entities/Category';
import { CatalogItemCheckbox } from 'features/CatalogItemCheckbox';
import { useGetItemDataUpdate } from 'features/CatalogItemCheckbox/lib/useGetItemDataUpdate';
import { useCatalogIdentity } from 'shared/lib/contexts/CatalogIdentityContext';
import { getPrice } from 'shared/lib/getPrice/getPrice';
import { getTermFromCode } from 'shared/lib/unitOfMeasureUtility/unitOfMeasureUtility';
import { CatalogItemImage } from 'shared/ui/CatalogItemImage';
import { FindReplacementLink } from 'shared/ui/FindReplacementLink/FindReplacementLink';
import { useImageLightbox } from 'shared/ui/ImageLightbox';
import { UnavailableTooltipContent } from 'shared/ui/ItemAvailabilityModal';
import { DynamicTooltip } from 'shared/ui/Tooltip/ui/DynamicTooltip';

import {
    INVENTORY_THUMB_PX,
    columnWidths,
    inventoryDataCellSx,
    inventoryItemTitleSx,
    inventoryUomCellTypographySx,
} from '../../lib/inventoryTableRowSpec';

interface OrderInventoryTableRowProps {
    item: CatalogItem;
    canSeePrices: boolean;
    onExpandRow: () => void;
}

export const OrderInventoryTableRow = ({
    item,
    canSeePrices,
    onExpandRow,
}: OrderInventoryTableRowProps) => {
    const catalogIdentity = useCatalogIdentity();
    const itemUid = getItemUid(item, catalogIdentity);
    const { isActive, idToEdit, itemIsInOrder, qty } = useGetItemDataUpdate({
        item,
    });
    const { openLightbox } = useImageLightbox();
    const { availabilityStatus, isAvailable } = useItemAvailability(
        item.proposals,
    );

    return (
        <TableRow
            data-testclass="inventory-item-row"
            onClick={() => onExpandRow()}
            sx={{
                cursor: 'pointer',
                transform: 'translate(0)',
                clipPath: ' inset(0)',
                boxShadow: '0px 2px 6px 0px rgba(0, 0, 0, 0.10)',
                borderRadius: '4px',
                '& td': {
                    overflow: 'hidden',
                    bgcolor: 'background.paper',
                    '&:first-of-type': {
                        borderRadius: '4px 0 0 4px',
                    },
                    '&:last-of-type': {
                        borderRadius: '0 4px 4px 0',
                    },
                },
            }}
        >
            <TableCell
                padding="checkbox"
                sx={{
                    ...inventoryDataCellSx,
                    width: columnWidths.thumbnail,
                    padding: '0 0 0 20px',
                }}
            >
                <Box
                    display="flex"
                    alignItems="center"
                    onClick={(e) => {
                        e.stopPropagation();
                        openLightbox(item.thumbnailUrl);
                    }}
                    sx={{
                        width: INVENTORY_THUMB_PX,
                        height: INVENTORY_THUMB_PX,
                        borderRadius: '4px',
                        cursor: 'pointer',
                        flexShrink: 0,
                    }}
                >
                    <CatalogItemImage
                        src={item.thumbnailUrl}
                        height={`${INVENTORY_THUMB_PX}px`}
                        width={`${INVENTORY_THUMB_PX}px`}
                    />
                </Box>
            </TableCell>
            <TableCell
                sx={{
                    ...inventoryDataCellSx,
                    wordWrap: 'break-word',
                    boxSizing: 'border-box',
                }}
            >
                <Box display="flex" flexDirection="column" gap="4px">
                    <Typography
                        data-testclass="inventory-item-title"
                        sx={inventoryItemTitleSx}
                    >
                        {item.title}
                    </Typography>
                    <CategoryLabel
                        data-testclass="inventory-item-category"
                        categoryId={item.categoryId}
                    />
                </Box>
            </TableCell>
            <TableCell
                align="center"
                sx={{
                    ...inventoryDataCellSx,
                    width: columnWidths.sku,
                    boxSizing: 'content-box',
                }}
                data-testclass="inventory-item-sku"
            >
                <Typography
                    typography="openSans.body"
                    sx={(theme) => ({
                        color: alpha(theme.palette.common.black, 0.85),
                    })}
                >
                    {item.sku}
                </Typography>
            </TableCell>
            <TableCell
                align="center"
                sx={{
                    ...inventoryDataCellSx,
                    width: columnWidths.qtyPerUom,
                    boxSizing: 'content-box',
                }}
                data-testclass="inventory-item-qty-per-uom"
            >
                <Typography
                    typography="openSans.body"
                    align="center"
                    sx={(theme) => ({
                        color: alpha(theme.palette.common.black, 0.85),
                    })}
                >
                    {item.qtyPerUom}
                </Typography>
            </TableCell>
            <TableCell
                align="center"
                sx={{
                    ...inventoryDataCellSx,
                    width: '52px',
                    maxWidth: '52px',
                    boxSizing: 'content-box',
                }}
                data-testclass="inventory-item-uom"
            >
                <Typography align="center" sx={inventoryUomCellTypographySx}>
                    {getTermFromCode(item.uom, true)}
                </Typography>
            </TableCell>
            <TableCell
                sx={{
                    ...inventoryDataCellSx,
                    width: columnWidths.minOnHand,
                    boxSizing: 'content-box',
                }}
                align="center"
                data-testclass="inventory-item-min-on-hand"
            >
                <Typography
                    typography="openSans.body"
                    sx={(theme) => ({
                        color: alpha(theme.palette.common.black, 0.85),
                    })}
                >
                    {item.minAllowedInStock || 0}
                </Typography>
            </TableCell>
            {canSeePrices && (
                <TableCell
                    sx={{
                        ...inventoryDataCellSx,
                        width: '84px',
                        maxWidth: '84px',
                        boxSizing: 'content-box',
                    }}
                    align="right"
                    data-testclass="inventory-item-price"
                >
                    <Typography
                        typography="openSans.body"
                        sx={(theme) => ({
                            color: alpha(theme.palette.common.black, 0.85),
                        })}
                    >
                        {getPrice(item.price)}
                    </Typography>
                </TableCell>
            )}
            <TableCell
                align="right"
                sx={{
                    ...inventoryDataCellSx,
                    minWidth: columnWidths.buttons,
                    whiteSpace: 'nowrap',
                }}
                onClick={(e) => e.stopPropagation()}
            >
                <Box
                    display="flex"
                    justifyContent="flex-end"
                    alignItems="center"
                    gap="20px"
                >
                    {isAvailable ? (
                        <CatalogItemCheckbox
                            showQtyCheckbox
                            isActive={isActive}
                            quickEdit
                            qty={qty}
                            selectionKey={itemUid}
                            itemUid={itemUid}
                            catalogItemId={item.id}
                            itemIdToEdit={idToEdit}
                            isInOrder={itemIsInOrder}
                            source="inventory"
                        />
                    ) : (
                        <>
                            <FindReplacementLink />
                            <DynamicTooltip
                                maxWidth={400}
                                interactive
                                containerStyle={{
                                    display: 'inline-flex',
                                }}
                                content={() => (
                                    <UnavailableTooltipContent
                                        availabilityStatus={availabilityStatus}
                                        hideReplacementLink
                                    />
                                )}
                            >
                                <IconButton
                                    data-testclass="item-availability-info-icon"
                                    aria-label="Item availability info"
                                    size="small"
                                    sx={{ p: 0 }}
                                >
                                    <InfoOutlinedIcon
                                        sx={{
                                            fontSize: '20px',
                                            color: 'error.light',
                                        }}
                                    />
                                </IconButton>
                            </DynamicTooltip>
                        </>
                    )}
                </Box>
            </TableCell>
        </TableRow>
    );
};
